// Session 55 — orchestration layer for the material-request email lifecycle.
//
// Why this file exists: submit (trigger #1) and receive (trigger #4) each have two
// independent call sites (RequestMaterialsPage.js + InventoryActionCatalog.js for #1;
// RequestsInboxPage.js + InventoryActionCatalog.js for #4). Rather than duplicate
// "look up recipients, pick a template, call sendEmail" at each of the six call sites,
// every trigger point calls one function here. Fix a bug or change copy once, and every
// caller gets it — see Issue #124 and the corresponding Engineering Standards entry for
// the reasoning (extract shared *mutation/side-effect* logic; duplicated UI can stay
// duplicated when the contexts genuinely differ).
//
// CTA note, rewritten at Session 78h — #126 shipped and the note above it was wrong
// in both of its premises. It said the deep link depended on MaterialRequestDetailPage.js
// and on a Login.js redirect-after-auth fix. The first file does not exist and never did;
// #212 solved the same need with the Requests inbox. The second was never needed either,
// because the destination is a real route — /go/:linkTo/:linkId? — so it lives in the
// path, and Login.js's `from.pathname` read carries it through the sign-in round-trip
// untouched. A query string would not have survived, which is the whole reason for the
// route. See docs/gates/Stede_EmailDeepLinks_Gate_126.html, Finding 2.
//
// Every function below now takes `requestId` — material_requests.id, the same value the
// paired sendNotification() call already writes to notifications.link_id. That is gate
// decision G: the email and the in-app bell derive their destination from the same two
// columns, so they cannot drift the first time a new notification type ships.

import {
  sendEmail,
  getBranchManagerEmails,
  getRequestApproverEmails,
  getPurchasingEmails,
  getAdminExecutiveEmails,
  getPurchasingQueueActionEmails,
  emailRestockRequest,
  emailRestockRequestOffCatalog,
  emailRequestApprovedForPurchasing,
  emailRequestOrdered,
  emailRequestReceived,
  emailInvoiceDiscrepancy,
  emailPurchaseApprovalNeeded,
  emailPurchaseApprovalDenied,
  emailPurchaseApprovalGranted,
} from './notify';

// Trigger #1 — submission. Fires from RequestMaterialsPage.handleSubmit() and
// InventoryActionCatalog.submitRequest() (mode='request'). Goes to the requesting
// branch's managers.
// `lines` (Session 79.4, gate D10) is [{ name, qty, uom, isOffCatalog }] and is
// what makes a per-item table possible at all: the caller used to comma-join the
// lines into one string, so the template could only ever print what it was
// handed. Fewer than two lines and it is inert -- single-item email is unchanged.
export async function notifyRequestSubmitted({
  branchId, branchName, requestNumber, requesterName,
  isOffCatalog, itemName, description, qty, uom, qtyOnHand, vendorSuggestion, photoUrl, requestId, lines,
}) {
  const emails = await getBranchManagerEmails(branchId);
  if (!emails.length) return;
  // #218: qtyOnHand is emailRestockRequest()'s 7th positional argument and was never
  // passed here, so the "Qty on hand" row has been suppressed on every restock approval
  // email since Session 55. Off-catalog has no on-hand count by definition — the item is
  // not in the catalog — so its template neither takes nor needs the value.
  const html = isOffCatalog
    ? emailRestockRequestOffCatalog(description, requestNumber, branchName, requesterName, qty, uom, vendorSuggestion, photoUrl, requestId)
    : emailRestockRequest(itemName, requestNumber, branchName, requesterName, qty, uom, qtyOnHand ?? null, requestId, lines);
  await sendEmail(emails, `Approval needed — ${requestNumber}`, html);
}

// Trigger #2 — approval. Fires from RequestsInboxPage.handleApprove(). Goes to
// purchasing, not the branch — this is a new recipient audience, no prior template
// or call site targeted purchasing directly.
export async function notifyRequestApproved({
  requestNumber, branchName, approverName, isOffCatalog, itemName, description, qty, uom, requestId, lines,
}) {
  const emails = await getPurchasingEmails();
  if (!emails.length) return;
  const html = emailRequestApprovedForPurchasing(itemName, requestNumber, branchName, approverName, qty, uom, isOffCatalog, description, requestId, lines);
  await sendEmail(emails, `Ready to order — ${requestNumber}`, html);
}

// Trigger #3 — marked ordered. Fires from PurchasingQueuePage.submitMarkOrdered(),
// replacing a previously broken sendEmail(null, ...) call that was a guaranteed no-op.
//
// AUDIENCE NARROWED Session 78n8.3 (#413), from getBranchManagerEmails() to
// getRequestApproverEmails() — COLBY'S DECISION, and it overturned the
// recommendation, which was to leave this alone and match the new in-app
// notification to it instead.
//
// His rule: *"all the notifications and emails should be scoped for the managers
// who can act on them"*, and on the wider audience — *"a non approving manager
// doesnt need notifications they cant act on. thats pointless and will make
// someone start ignoring their notifications."* That is the stronger argument:
// consistency between the email and the bell is worth something, but not as much
// as every notification in the bell being one the reader can act on.
//
// WHAT ACTUALLY CHANGED, stated precisely because it is narrower than it sounds
// and the branch half was ALREADY CORRECT: getBranchManagerEmails() filtered on
// branch_id, so a Houston manager has never received an Austin order email. The
// door that was open is the DEPARTMENT one — a department-scoped manager at the
// right branch, who cannot approve this particular request, was being emailed
// about it. They now get nothing. A branch-scoped manager still gets everything
// at their branch, always, because they can approve anything there.
//
// This is the same set as getRequestApproverIds() and it MUST agree with
// canApprove() in src/lib/canApprove.js — see the note above fetchApprovers()
// in notify.js. Change both or neither.
//
// `departmentId` is required for that scoping and is NEW to this signature. A
// caller that omits it resolves to branch-scoped managers alone, which is the
// safe direction: those people can approve anything at the branch, so nobody who
// should have been told is missed.
export async function notifyRequestOrdered({
  branchId, departmentId, branchName, requestNumber, itemName, vendorName, poRef, eta, orderedByName, requestId,
}) {
  const emails = await getRequestApproverEmails(branchId, departmentId);
  if (!emails.length) return [];
  const html = emailRequestOrdered(itemName, requestNumber, branchName, vendorName, poRef || '—', eta || '—', orderedByName, requestId);
  await sendEmail(emails, `Order placed — ${requestNumber}`, html);
  // #395, Session 78n8.2 -- RETURNS the addresses it mailed, so the caller can
  // avoid sending the same person a second email about the same order minutes
  // later. That happens whenever the requester is also a manager at their own
  // branch, which is Colby's double-notification case and is ordinary rather
  // than rare: a manager raising a request they then approve. Callers that
  // ignore the return behave exactly as before.
  return emails.map(e => String(e).toLowerCase());
}

// Trigger #4 — marked received. Fires from RequestsInboxPage's ReceiveSheetPortal
// .handleSubmit() and InventoryActionCatalog.submitReceive(). Goes to branch managers
// ONLY, by explicit decision — the requester already sees this in-app via the existing
// sendNotification() call at both sites, and does not also receive the email.
export async function notifyRequestReceived({
  branchId, branchName, requestNumber, itemName, qtyReceived, uom, receivedByName, requestId,
}) {
  const emails = await getBranchManagerEmails(branchId);
  if (!emails.length) return;
  const html = emailRequestReceived(itemName, requestNumber, branchName, qtyReceived, uom, receivedByName, requestId);
  await sendEmail(emails, `Received — ${requestNumber}`, html);
}

// Trigger #5 — Issue #191, invoice price discrepancy. Fires from PurchasingQueuePage's
// submitEnterInvoice() only when the discrepancy check trips. Goes to admin+executive,
// matching the in-app sendNotification() call already firing at the same call site —
// this is that same event's email half, not a separate decision point.
export async function notifyInvoiceDiscrepancy({
  requestNumber, branchName, itemName, orderedPrice, invoicePrice, invoiceRef, requestId,
}) {
  const emails = await getAdminExecutiveEmails();
  if (!emails.length) return;
  const html = emailInvoiceDiscrepancy(itemName, requestNumber, branchName, orderedPrice, invoicePrice, invoiceRef, requestId);
  await sendEmail(emails, `Invoice discrepancy — ${requestNumber}`, html);
}

// New Session — Issue #210, trigger #6. Fires from PurchasingQueuePage's
// submitMarkOrdered() only when the order total exceeds the configured threshold.
export async function notifyPurchaseApprovalNeeded({
  branchName, requestNumber, itemName, orderTotal, thresholdAmount, requestId,
}) {
  const emails = await getAdminExecutiveEmails();
  if (!emails.length) return;
  const html = emailPurchaseApprovalNeeded(itemName, requestNumber, branchName, orderTotal, thresholdAmount, requestId);
  await sendEmail(emails, `Approval needed — ${requestNumber}`, html);
}

// New Session — Issue #210, trigger #7. Fires from PurchasingQueuePage's
// submitDenyPurchase() — goes to whoever can act on the Purchasing Queue again.
export async function notifyPurchaseApprovalDenied({
  branchName, requestNumber, itemName, reason, requestId,
}) {
  const emails = await getPurchasingQueueActionEmails();
  if (!emails.length) return;
  const html = emailPurchaseApprovalDenied(itemName, requestNumber, branchName, reason, requestId);
  await sendEmail(emails, `Order denied — ${requestNumber}`, html);
}

// New Session 74p - Issue #256, trigger #8. Fires from PurchasingQueuePage's
// submitApprovePurchase(). Goes to the same audience as the denial sibling above,
// deliberately: after either outcome the next action is somebody re-opening Mark
// Ordered, and that is admin, executive and purchasing, not the purchasing role
// alone. Approval no longer sends notifyRequestOrdered() - nothing has been ordered
// at this point, a human still has to contact the vendor.
export async function notifyPurchaseApprovalGranted({
  branchName, requestNumber, itemName, approvedAmount, approverName, requestId,
}) {
  const emails = await getPurchasingQueueActionEmails();
  if (!emails.length) return;
  const html = emailPurchaseApprovalGranted(itemName, requestNumber, branchName, approvedAmount, approverName, requestId);
  await sendEmail(emails, `Approved - ready to order - ${requestNumber}`, html);
}
